// Editor da "Rotina de Coleta e Devolução" de um Cliente (ADR-0014), em estado
// local. Usado dentro do modal de Cliente (novo ou edição) — a tela é ligada na
// Task C2. Uma grade semanal: linhas = horários, colunas = dias, cada célula
// alterna vazio → Coletar → Devolver → Ambos.

import type { DadosParada } from "../../domain/paradas.ts";
import type { Parada, AcaoParada } from "../../domain/snapshot.ts";
import { el, clear, botao } from "../ui.ts";
import { DIAS_DISPLAY } from "../../domain/cronograma.ts";
import { inputHora } from "./_cadastro.ts";

/** Rótulos do cabeçalho, na mesma ordem de `DIAS_DISPLAY` (seg → dom). */
const DIAS_CABECALHO = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

/** Ordem do ciclo da célula ao clicar. `null` = sem parada. */
const CICLO: readonly (AcaoParada | null)[] = [null, "coletar", "devolver", "ambos"];

/** Ação fixa das linhas de `paradas()` — mantém a saída determinística. */
const ACOES: readonly AcaoParada[] = ["coletar", "devolver", "ambos"];

const TEXTO_ACAO: Record<AcaoParada, string> = {
  coletar: "Coletar",
  devolver: "Devolver",
  ambos: "Ambos",
};

export interface ParadasEditor {
  element: HTMLElement;
  paradas(): DadosParada[];
  devolucaoNoMesmoDia(): boolean;
}

export function paradasEditor(
  paradasAtuais: readonly Parada[],
  devolucaoNoMesmoDiaAtual: boolean,
): ParadasEditor {
  // Estado: horário → (dia → ação). A ordem de inserção das chaves é a ordem
  // das linhas na grade; começa pelos horários distintos de `paradasAtuais`.
  const grade = new Map<string, Map<number, AcaoParada>>();
  for (const h of [...new Set(paradasAtuais.map((p) => p.horario))].sort()) {
    grade.set(h, new Map());
  }
  for (const p of paradasAtuais) {
    const linha = grade.get(p.horario)!;
    for (const dia of p.diasSemana) linha.set(dia, p.acao);
  }

  /** Reinsere as chaves de `grade` em ordem de horário (após incluir uma linha). */
  const reordenar = (): void => {
    const ordenado = [...grade.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    grade.clear();
    for (const [h, linha] of ordenado) grade.set(h, linha);
  };

  const gradeEl = el("div", { class: "paradas-grade" });

  const vazio = el(
    "p",
    { class: "muted paradas-vazio" },
    'Nenhum horário. Use "+ Adicionar Horário".',
  );

  const desenharGrade = (): void => {
    vazio.hidden = grade.size > 0;
    gradeEl.hidden = grade.size === 0;
    clear(gradeEl);
    gradeEl.append(el("div", { class: "pg-h" }));
    for (const rot of DIAS_CABECALHO) gradeEl.append(el("div", { class: "pg-h" }, rot));

    for (const [horario, linha] of grade) {
      const btnRemover = el(
        "button",
        { type: "button", class: "pg-rm", "aria-label": `Remover o Horário ${horario}` },
        "×",
      );
      btnRemover.addEventListener("click", () => {
        grade.delete(horario);
        desenharGrade();
      });
      gradeEl.append(
        el("div", { class: "pg-hora" }, el("span", {}, horario), btnRemover),
      );

      for (const dia of DIAS_DISPLAY) {
        const cel = el("button", { type: "button" });
        const pintar = (): void => {
          const acao = linha.get(dia) ?? null;
          cel.className = acao ? `parada-cel parada-cel-${acao}` : "parada-cel";
          cel.textContent = acao ? TEXTO_ACAO[acao] : "";
        };
        pintar();
        cel.addEventListener("click", () => {
          const atual = linha.get(dia) ?? null;
          const proxima = CICLO[(CICLO.indexOf(atual) + 1) % CICLO.length] ?? null;
          if (proxima === null) linha.delete(dia);
          else linha.set(dia, proxima);
          pintar();
        });
        gradeEl.append(cel);
      }
    }
  };

  // --- adicionar horário ---------------------------------------------------
  const inputHoraNova = inputHora();
  const btnConfirmar = botao("secundario", {}, "Adicionar");
  const areaAdd = el("div", { class: "paradas-add-hora" }, inputHoraNova, btnConfirmar);
  areaAdd.hidden = true;

  const erro = el("p", { class: "form-erro" });
  erro.hidden = true;

  const btnAdd = botao(
    "secundario",
    {},
    "+ Adicionar Horário",
  );
  btnAdd.addEventListener("click", () => {
    btnAdd.hidden = true;
    areaAdd.hidden = false;
    inputHoraNova.focus();
  });
  btnConfirmar.addEventListener("click", () => {
    const h = inputHoraNova.value;
    if (!h) return;
    if (grade.has(h)) {
      erro.textContent = "Esse horário já está na grade.";
      erro.hidden = false;
      return;
    }
    grade.set(h, new Map());
    reordenar();
    inputHoraNova.value = "";
    erro.hidden = true;
    areaAdd.hidden = true;
    btnAdd.hidden = false;
    desenharGrade();
  });

  // --- legenda -----------------------------------------------------------
  const swatch = (classe: string): HTMLElement => el("i", classe ? { class: classe } : {});
  const legenda = el(
    "div",
    { class: "paradas-legenda" },
    el("span", {}, swatch(""), "Sem parada"),
    el("span", {}, swatch("parada-cel-coletar"), "Coletar"),
    el("span", {}, swatch("parada-cel-devolver"), "Devolver"),
    el("span", {}, swatch("parada-cel-ambos"), "Ambos"),
  );

  // --- folga da devolução ----------------------------------------------
  const radioNunca = el("input", { type: "radio", name: "parada-folga" });
  const radioMesmoDia = el("input", { type: "radio", name: "parada-folga" });
  radioNunca.checked = !devolucaoNoMesmoDiaAtual;
  radioMesmoDia.checked = devolucaoNoMesmoDiaAtual;
  const folga = el(
    "div",
    { class: "prazo-padrao" },
    el("strong", {}, "Prazo da Devolução"),
    el("label", { class: "check" }, radioNunca, " Nunca no mesmo dia da coleta"),
    el("label", { class: "check" }, radioMesmoDia, " A devolução pode ser no mesmo dia"),
  );

  const element = el(
    "fieldset",
    { class: "paradas-editor" },
    el("legend", {}, "Rotina de Coleta e Devolução"),
    gradeEl,
    vazio,
    areaAdd,
    erro,
    btnAdd,
    legenda,
    folga,
  );

  desenharGrade();

  const paradas = (): DadosParada[] => {
    const saida: DadosParada[] = [];
    for (const [horario, linha] of grade) {
      const porAcao = new Map<AcaoParada, number[]>();
      for (const [dia, acao] of linha) {
        const dias = porAcao.get(acao) ?? [];
        dias.push(dia);
        porAcao.set(acao, dias);
      }
      for (const acao of ACOES) {
        const dias = porAcao.get(acao);
        if (!dias || dias.length === 0) continue;
        saida.push({
          diasSemana: [...dias].sort((a, b) => a - b),
          horario,
          acao,
        });
      }
    }
    return saida;
  };

  return {
    element,
    paradas,
    devolucaoNoMesmoDia: () => radioMesmoDia.checked,
  };
}
